import { useSelector, shallowEqual } from "react-redux";
import { usePathname } from "next/navigation";
import { cleverTapCommonWords } from "../../../analytics/clevertapConstants";
import { CAKE_TYPE } from "../../../constants/pageConstants/pdpConstants";
import { COUNTRIES } from "../../../constants/common/common";
import { checkMultiUploadedImageCount } from "../../../utils/pdp/personalization";
import { calDiscountPercentage, getProductDeliveryType, processDeliveryDate } from "../../../utils/common";
import { isDateTodayOrTomorrow } from "../../../utils/common/deliveryDate";

/**
 * Custom React hook to generate CleverTap tracking data for the product detail page (PDP).
 *
 * This hook extracts necessary state from the Redux store and combines it with passed-in
 * parameters to generate tracking data objects for CleverTap events.
 *
 * @param {Object} params - Parameters for generating CleverTap data.
 * @param {Object} params.currentItem - The current product item being viewed.
 * @param {Object} params.serverData - Data from the server, including URL identifiers.
 * @param {string} params.deliveryType - The type of delivery (e.g., standard, express).
 * @param {Object} params.selectedCurrency - The selected currency object with its details.
 * @param {Array} params.deliveryDates - Array of available delivery dates for the product.
 * @param {boolean} params.isOutOfStock - Flag indicating if the product is out of stock.
 * @param {boolean} params.isSameSku - Flag indicating if the SKU is the same as the previously selected one.
 * @param {string} params.catalogId - The catalog ID for the product (e.g., "india" or other regions).
 *
 * @returns {Object} An object containing CleverTap data objects:
 * @returns {Object} return.commonCleverTapData - Common tracking data shared across events.
 * @returns {Object} return.cleverTapPageData - Tracking data for the PDP page view.
 * @returns {Object} return.cleverTapPDPEventData - Tracking data for PDP-specific events (e.g., delivery details).
 */
const useCleverTapPDPData = ({
  currentItem,
  serverData,
  selectedCurrency,
  deliveryDates,
  isOutOfStock,
  isSameSku,
  catalogId,
}) => {
  const isInternational = currentItem?.catalogId !== COUNTRIES.INDIA;
  const checkLoginStatus = useSelector((state) => state.fus.loginDetail?.loggedin);
  const {
    nextAvailableDifference,
    pincode,
    uploadFinalStatus,
    deliveryDetailsData,
    address,
    selectedIntLocation,
    clientVariantType,
    multiImageConfig,
  } = useSelector(
    (state) => ({
      nextAvailableDifference: state?.shippingDateAndSlotSelection?.nextAvailableDifference,
      pincode: state?.locationLock?.selectedDomesticLocation?.pincode,
      uploadFinalStatus: state?.personalization?.uploadFinalStatus,
      deliveryDetailsData: state?.shippingDateAndSlotSelection?.selectedDeliveryDetails,
      address: state?.locationLock?.selectedDomesticLocation?.address,
      selectedIntLocation: state?.locationLock?.selectedIntLocation,
      clientVariantType: state?.pdp?.clientVariantType,
      multiImageConfig: state?.personalization?.multiImageConfig,
    }),
    shallowEqual,
  );

  const currentPath = usePathname();
  const currentUrlIdentifier = currentPath.split("/").pop();
  const previousPathName =
    typeof window !== "undefined" && document?.referrer ? new URL(document?.referrer)?.pathname : "";
  const pathname = typeof window !== "undefined" && window.location.pathname ? window.location.pathname : "";
  const completePathName = typeof window !== "undefined" && window.location.href ? window.location.href : "";
  const { isDateToday, isDateTomorrow } = isDateTodayOrTomorrow(new Date(currentItem?.deliveryDate));
  const extractProductImages =
    currentItem?.productContent?.productExtraLargeImages?.[0] || currentItem?.productContent?.productImages?.[0];
  const discountPercentage = calDiscountPercentage(
    currentItem?.productPrice?.listPrice,
    currentItem?.productPrice?.price,
  );
  const commonCleverTapData = {
    complete_url: completePathName,
    clean_url: pathname,
    page_type: cleverTapCommonWords.PDP,
    page_name: serverData?.urlIdentifier,
    product_name: currentItem?.productName,
    pid: currentItem?.productId,
    product_category: currentItem?.primaryCategoryId,
    is_variant_available: currentItem?.isVariant === "Y",
    is_egg: clientVariantType?.toUpperCase() === CAKE_TYPE.WITH_EGG ?? false,
    delivery_type: getProductDeliveryType(currentItem?.primaryCategoryId),
    product_price: currentItem?.productPrice?.price,
    currency: selectedCurrency?.currency?.id ?? "INR",
    strike_price: currentItem?.productPrice?.listPrice,
    discount_percentage: discountPercentage,
    product_rating: currentItem?.productReviewsAndRatings?.ratingValue,
    product_review_count: currentItem?.productReviewsAndRatings?.totalReview,
    is_product_available: currentItem?.stockAvailable,
    is_international: isInternational,
    city: isInternational ? selectedIntLocation?.location : "",
    area: isInternational ? "" : address,
    pincode: isInternational ? "" : pincode,
    system_pincode: isInternational ? "" : pincode,
    country: catalogId,
    sku_id: currentItem?.skuCode,
    login_status: checkLoginStatus,
    image_url: `${process.env.NEXT_PUBLIC_IMAGE_DOMAIN}${extractProductImages}`,
  };

  const cleverTapPageData = {
    ...commonCleverTapData,
    internal_source_type: previousPathName ?? "",
    internal_source: previousPathName ?? "",
    is_want_same_day_visible:
      currentItem?.catalogId === COUNTRIES.INDIA && !isOutOfStock && deliveryDates?.deliveryDates?.length > 0,
    is_same_sku: isSameSku,
  };
  const uploadedImageCount = checkMultiUploadedImageCount(multiImageConfig);
  const cleverTapPDPEventData = {
    ...commonCleverTapData,
    date_of_delivery: processDeliveryDate(deliveryDetailsData?.selectedDate),
    is_date_today: isDateToday,
    is_date_tomorrow: isDateTomorrow,
    next_available_date_difference: isInternational ? "" : nextAvailableDifference?.nextAvailableDateDiff,
    delivery_shipping: deliveryDetailsData?.currentShippingMethod?.shippingMethodName || "",
    delivery_shipping_fee: deliveryDetailsData?.timeSlots?.timeSlots?.shippingPrice ?? 0,
    delivery_slot: deliveryDetailsData?.timeSlots?.formatTime || "",
    earliest_delivery_date: deliveryDates?.deliveryDates?.[0] ?? "",
    photo_count: uploadedImageCount || 0,
    is_photo_uploaded: uploadFinalStatus,
    is_variant_selected: serverData?.urlIdentifier !== currentUrlIdentifier,
    attribution_url: typeof window !== "undefined" && document?.referrer ? document?.referrer : "",
  };

  return {
    commonCleverTapData,
    cleverTapPageData,
    cleverTapPDPEventData,
  };
};

export default useCleverTapPDPData;
